import { useEffect, useRef, useState } from "react";
import { ProviderAppTile } from "./ProviderAppTile";
import { VERIFIED_INSURERS, RAIL_ONLY_INSURERS } from "../lib/verifiedInsurers";
import { openAffiliatePartner } from "../lib/partnerRouting";

// Two slow, opposing rows of the same app tiles. Purely visual (the demo grid
// above is the accessible list), so the tiles are aria-hidden. Paused on
// hover and whenever it is off-screen; static under reduced motion (CSS).
// 13 unique insurers, interleaved so neighbours differ; row 2 is offset by
// half the set so the same insurer never lines up across rows.
const ORDER = VERIFIED_INSURERS.flatMap((ins, i) => (RAIL_ONLY_INSURERS[i] ? [ins, RAIL_ONLY_INSURERS[i]] : [ins]));
const ROW2 = [...ORDER.slice(6), ...ORDER.slice(0, 6)].reverse();
export const INSURIFY_AUTO_URL = "https://insurify.com/start/auto/partner/carsavr/?utm_source=carsavr&utm_medium=auto";

const Row = ({ items, reverse, paused, size, labelClass }) => (
  <div className="marquee-mask overflow-hidden py-1.5">
    <div className={`rail-track flex w-max gap-5 sm:gap-7 ${reverse ? "rail-track-reverse" : ""} ${paused ? "rail-paused" : ""}`}>
      {[...items, ...items, ...items, ...items].map((ins, i) => (
        <ProviderAppTile key={`${ins.slug}-${i}`} insurer={ins} state="resting" size={size} labelClass={labelClass} decorative />
      ))}
    </div>
  </div>
);

// compact: one row, small tiles, dark-background text — used in the top trust strip.
export const ProviderRail = ({ compact = false, href, testId = "provider-rail" }) => {
  const ref = useRef(null);
  const [offscreen, setOffscreen] = useState(true);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setOffscreen(!e.isIntersecting));
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const rowProps = compact ? { size: "sm", labelClass: "text-slate-300" } : { size: "rail" };
  const rows = (
    <div aria-hidden="true" className="space-y-1">
      <Row items={ORDER} paused={offscreen} {...rowProps} />
      {!compact && <Row items={ROW2} reverse paused={offscreen} {...rowProps} />}
    </div>
  );
  return (
    <div ref={ref} className={`provider-rail ${compact ? "" : "mt-10 lg:mt-12"}`} data-testid={testId}>
      <p className={`text-center text-sm mb-4 ${compact ? "text-slate-300" : "text-slate-600"}`}>
        Compare rates from <span className={`font-semibold ${compact ? "text-white" : "text-slate-900"}`}>120+ auto insurers</span> through our partner{" "}
        {href && !compact ? (
          <a href={href} target="_blank" rel="sponsored noopener noreferrer" onClick={(e) => openAffiliatePartner({ url: href, partnerName: "Insurify", source: `${testId}-text`, category: "insurance", event: e })} data-testid={`${testId}-text-link`} className="font-semibold text-brand-emerald-strong underline underline-offset-2 hover:text-brand-emerald-dark">Insurify</a>
        ) : "Insurify"}, including:
      </p>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={(e) => openAffiliatePartner({ url: href, partnerName: "Insurify", source: testId, category: "insurance", event: e })} aria-label="Compare 120+ auto insurers on Insurify (opens in a new tab)" data-testid={`${testId}-link`} className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981]">
          {rows}
        </a>
      ) : rows}
      {!compact && <p className="mt-3 text-center text-xs text-slate-500">Availability varies by driver, vehicle, location and eligibility.</p>}
    </div>
  );
};
